import {
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

import {
  CalendarIcon,
  CheckIcon,
  ChevronDownIcon,
  CircleArrowDownIcon,
  CircleQuestionMarkIcon,
  Trash2Icon,
  UserIcon,
} from "lucide-react";

import { formatDateAt } from "simplydo/core/dates";
import {
  DEADLINE_SPECIAL_FIELD_IDS,
  getDeadlineEditableFieldTitles,
  hasDeadlineFieldRestriction,
  isFieldEditableUnderDeadline,
} from "simplydo/core/ideas";
import {
  getSectionSubmissibility,
  getSubmissibilityColor,
  getSubmissibilityProgress,
  getSubmissibilitySectionKey,
  isSubmissibilitySectionComplete,
} from "simplydo/core/submissibility";
import type { OpenAPI } from "simplydo/interfaces";
import { MarkdownViewer, RichTextViewer } from "simplydo/react/richtext";
import { css, cx, styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import {
  Accordion,
  AccordionContent,
  AccordionTitle,
} from "simplydo/react/ui/accordion";
import { Button, ButtonGroup } from "simplydo/react/ui/button";
import { Card, CardContent } from "simplydo/react/ui/card";
import { Divider } from "simplydo/react/ui/divider";
import { H3, H4, H5 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Label } from "simplydo/react/ui/label";
import { List, ListItem } from "simplydo/react/ui/list";
import {
  Message,
  MessageContent,
  MessageHeader,
} from "simplydo/react/ui/message";
import { Placeholder, PlaceholderLine } from "simplydo/react/ui/placeholder";
import { Popup } from "simplydo/react/ui/popup";

import { useAppSelector } from "store";
import util from "utils/utils";

import { SideNavContainer } from "components/challenges/YourIdeas";
import { useAssessmentEditor } from "components/ideas/hooks/useAssessmentEditor";
import CircularProgress from "components/lib/CircularProgress";
import IPStatementInfo from "components/lib/IPStatementInfo";
import { SubmitIdeaButton } from "components/lib/Ideas";
import IdeaImpactStatement from "components/lib/Ideas/ImpactStatements/IdeaImpactStatement";
import { SideNavLink } from "components/lib/SideNav";
import { Sticky, TooltipButton } from "components/lib/UI";

import IdeaAttachments from "../Attachments";
import IdeaComments from "../Comments";
import IdeaBusinessProfile from "../Components/IdeaBusinessProfile";
import IdeaSectionHeader from "../SectionHeader";
import { useChildIdState, useChildIdea, useIdea } from "../hooks/useIdea";
import type { IdeaExternalChanges } from "../types";
import useScrollTracking, { type ItemRefs } from "../useIdeaScrollTracking";
import { AssessorToolbar } from "./AssessorToolbar";
import Custom from "./Custom";
import IdeaNameField from "./Name";

const IdeaSection = styled.div`
  @media print {
    page-break-after: always;
    margin: 2cm 30px;
  }

  &.is-assessment {
    border: 4px solid ${({ theme }) => theme.colors.violet.solid};
    border-radius: 0.3rem;
    border-bottom-width: 3px;
    border-top-width: 3px;

    ${Card} {
      margin: 0;
    }
  }
`;

const ChildIdeaChooser = styled.div`
  z-index: 999;
  > .title {
    opacity: 0.8;
    display: block;
    margin: 0 5px;
    font-weight: bold;
    margin-bottom: 5px;
  }
  > .buttons {
    display: flex;
    flex-direction: row;
    overflow-x: auto;
    gap: 5px;
    .ui.button.merge-button-tab {
      width: 250px;
    }
    .button-idea-name {
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
`;

const stickyMergeIdeaHeaderClass = css`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px !important;
  .jump-to-section-button {
    margin-top: 5px;
    height: 25px;
    padding: 5px 12.5px;
  }
  ${ChildIdeaChooser} {
    width: 100%;
  }
`;

export type ChallengeIdea = OpenAPI.Schemas["ResponseGetIdea"];

type BodyProps = {
  idea?: ChallengeIdea;
  setSectionRef?: (ref: HTMLDivElement | null, key: string) => void;
  sectionRefs?: RefObject<ItemRefs>;
  setFieldRef?: (ref: HTMLDivElement | null, key: string) => void;
  fieldRefs?: RefObject<ItemRefs>;
  loading?: boolean;
  updateIdea: (value: unknown, path: string[], replace?: boolean) => void;
  scrollToRequired?: (sectionId: string) => void;
  onSubmit?: (toSubmit: boolean) => void;
  isEditing?: boolean;
  isSaving?: boolean;
  hideComments?: boolean;
  externalChanges?: IdeaExternalChanges;
  isViewingMergedIdea?: boolean;
  unsaved?: boolean;
  scrollToSection?: (sectionId: string) => void;
  scrollToField?: (fieldId: string) => void;
  emptyRequiredField?: boolean;
};

type BodyContainerProps = BodyProps & {
  scrollRef?: RefObject<HTMLDivElement | null>;
  removeIdeaFromMerge?: (parentId: string, childId: string) => void;
  removingIdeaFromMerge?: boolean;
};

const BodyContainer = ({
  scrollRef,
  removeIdeaFromMerge,
  removingIdeaFromMerge,
  setSectionRef,
  sectionRefs,
  setFieldRef,
  fieldRefs,
  ...rest
}: BodyContainerProps) => {
  /*
    Code for handling merged idea bodies

    One idea is set as the "active" idea, and the content from that idea will be what is shown
  */
  const [idea] = useIdea();
  const location = useLocation();

  const [ideaOrChild, ideaOrChildQuery] = useChildIdea();
  const [childId, setChildId] = useChildIdState();

  const user = useAppSelector((state) => state.user);
  const { t } = useTranslation();
  const sectionScroll = useScrollTracking(sectionRefs, scrollRef, -180);
  const fieldScroll = useScrollTracking(fieldRefs, scrollRef, -180);

  const usingMergedIdea = useMemo(
    () => !!idea?.children?.length,
    [idea?.children],
  );

  const [prevSectionViewed, setPrevSectionViewed] = useState<string>("");
  const [isNearScrollTop, setIsNearScrollTop] = useState(true);

  useEffect(() => {
    const scrollElement = scrollRef?.current;
    const getScrollTop = () =>
      scrollElement ? scrollElement.scrollTop : window.scrollY;

    const handleScroll = () => {
      setIsNearScrollTop(getScrollTop() < 100);
    };

    handleScroll();

    if (scrollElement) {
      scrollElement.addEventListener("scroll", handleScroll, { passive: true });
      return () => scrollElement.removeEventListener("scroll", handleScroll);
    }

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [scrollRef]);

  const changeActiveChildIdea = useCallback(
    (childIdea: OpenAPI.Schemas["Idea"]) => {
      if (scrollRef?.current) {
        scrollRef.current.scrollTop = 0;
      } else {
        window.scrollTo(0, 0);
      }
      setPrevSectionViewed(sectionScroll.activeItemId ?? "");
      setChildId(childIdea._id);
    },
    [sectionScroll.activeItemId, scrollRef, setChildId],
  );

  // You can jump to the section that you were viewing in the previous idea, if the section exists in the new idea, and it's not the first section
  const canJumpToSectionInIdea = useMemo(() => {
    if (!childId || prevSectionViewed === "") {
      return false;
    }
    const sectionIds = Object.keys(sectionRefs?.current ?? {});
    const prevSectionIndex = sectionIds.findIndex(
      (sectionId) => sectionId === prevSectionViewed,
    );
    return (
      sectionIds.length > 0 &&
      sectionIds.indexOf(prevSectionViewed) !== -1 &&
      prevSectionIndex !== 0
    );
  }, [childId, prevSectionViewed, sectionRefs]);

  const onScrollToPrevSection = useCallback(
    (sectionId: string) => {
      setPrevSectionViewed("");
      sectionScroll.scrollToItem(sectionId);
    },
    [sectionScroll],
  );

  const prevSectionName = useMemo(
    () =>
      idea?.ownerIdeaTemplate?.body?.find(
        (section) => section.id === prevSectionViewed,
      )?.name,
    [idea?.ownerIdeaTemplate?.body, prevSectionViewed],
  );

  if (usingMergedIdea) {
    if (!childId) {
      return null;
    }
    return (
      <div>
        <Sticky
          top={
            location.pathname.startsWith(`/ideas/`)
              ? idea?.isSubmitted
                ? 110
                : 150
              : 5
          }
          bottom={undefined}
          style={{ zIndex: 100 }}
        >
          <Card fluid className={stickyMergeIdeaHeaderClass}>
            <CardContent>
              <ChildIdeaChooser>
                <span className="title">
                  Viewing merged {t("generic.idea")}...
                </span>
                <div className="buttons">
                  {idea?.ownerChildren?.map((childIdea) => (
                    <ButtonGroup key={childIdea._id}>
                      <Button
                        key={childIdea._id}
                        onClick={() => changeActiveChildIdea(childIdea)}
                        className="merge-button-tab"
                        color={
                          childIdea._id === childId ? "primary" : undefined
                        }
                        variant={
                          childIdea._id !== childId ? "outline" : "regular"
                        }
                      >
                        <div
                          style={{ display: "flex", flexDirection: "column" }}
                        >
                          <span className="button-idea-name">
                            {childIdea.name}
                          </span>
                          {childIdea?.owner ? (
                            <span style={{ opacity: 0.5, fontSize: "0.8em" }}>
                              By {childIdea?.owner?.profile?.fullName}
                            </span>
                          ) : null}
                        </div>
                      </Button>
                      {(util.canManageChallenge(user!, idea?.ownerChallenge) ||
                        util.hasPermission(
                          user!,
                          "challenge.mergeIdeas",
                          idea?.challenge,
                        )) &&
                      removeIdeaFromMerge ? (
                        <Popup
                          on="hover"
                          trigger={
                            <Button
                              onClick={() =>
                                removeIdeaFromMerge(idea?._id, childIdea._id)
                              }
                            >
                              <Icon icon={Trash2Icon} />
                            </Button>
                          }
                        >{`Remove this ${t("generic.idea")} from the merge`}</Popup>
                      ) : null}
                    </ButtonGroup>
                  ))}
                </div>
                {idea?.ownerChildren?.length !== idea?.children?.length ? (
                  <Message color="blue" size="small">
                    You are not able to view all {t("generic.ideas")} in this
                    merge. You may not have permission to view some of the{" "}
                    {t("generic.ideas")}, or they may have been deleted.
                  </Message>
                ) : null}
              </ChildIdeaChooser>
              {canJumpToSectionInIdea && isNearScrollTop ? (
                <div>
                  <Button
                    onClick={() => onScrollToPrevSection(prevSectionViewed)}
                    className="jump-to-section-button"
                    size="small"
                  >
                    <Icon icon={CircleArrowDownIcon} />
                    {`Jump to ${!prevSectionName ? "previous section" : `section "${prevSectionName}"`}`}
                  </Button>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </Sticky>
        {ideaOrChild &&
        user?._id &&
        (ideaOrChild.user === user._id ||
          ideaOrChild.collaborators?.includes(user._id)) &&
        !(idea?.user === user._id) ? (
          <Message color="blue">
            Your {t("generic.idea")} has been merged with other{" "}
            {t("generic.ideas")}; You are currently viewing the merge result.
            You can still view your individual {t("generic.idea")}{" "}
            <Link to={`/ideas/${childId}`}>here</Link>.
          </Message>
        ) : null}
        <Body
          setSectionRef={setSectionRef}
          setFieldRef={setFieldRef}
          isViewingMergedIdea
          scrollToSection={sectionScroll.scrollToItem}
          scrollToField={fieldScroll.scrollToItem}
          {...rest}
        />
        {!removingIdeaFromMerge ? (
          <div>
            <div ref={(ref) => setSectionRef?.(ref, "comments")} id="comments">
              <Divider hidden />
              <H4>Comments</H4>
              <IdeaComments forId={idea?._id} isViewingMergedIdea />
              <Divider hidden />
              <Divider hidden />
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <>
      {ideaOrChildQuery.isLoading || !ideaOrChild || !ideaOrChild._id ? null : (
        <>
          <AssessorToolbar
            isEditing={rest.isEditing}
            activeItemId={fieldScroll.activeItemId}
          />
        </>
      )}
      <Body
        setSectionRef={setSectionRef}
        setFieldRef={setFieldRef}
        scrollToSection={sectionScroll.scrollToItem}
        {...rest}
      />
    </>
  );
};

const Body = (props: BodyProps) => {
  const {
    setSectionRef,
    setFieldRef,
    updateIdea,
    scrollToRequired,
    onSubmit,
    isEditing,
    isSaving,
    unsaved,
    hideComments,
    externalChanges,
    isViewingMergedIdea,
    scrollToSection,
  } = props;
  const [idea, ideaQuery] = useChildIdea();
  const user = useAppSelector((state) => state.user);
  const theme = useTheme();
  const { t } = useTranslation();

  const {
    isAssessingSelectedRound,
    selectedAssessmentRoundId,
    urlAssessmentRoundId,
    assessmentState,
    submitAssessment,
    assessmentIsSubmissable,
    isSavingAssessment,
  } = useAssessmentEditor(idea);
  const hasCompletedConflictDeclaration =
    assessmentState?.conflictOfInterest?.hasConflictOfInterest !== undefined;

  const firstAssessmentSectionIndex = idea?.ownerIdeaTemplate?.body?.findIndex(
    (section) =>
      section.type === "assessment" &&
      (section.assessmentRoundId || "legacy") ===
        (selectedAssessmentRoundId || "legacy"),
  );

  const canEdit = util.canEditIdea(user!, idea);
  const isAuthor = util.isIdeaAuthor(user!, idea);
  const canViewEdi = util.hasPermission(
    user!,
    "org.viewEdi",
    idea?.ownerChallenge?.organisation,
  );
  const canBypassDeadlineFieldLock =
    util.hasPermission(user!, "challenge.editIdeas", idea?.challenge) ||
    util.hasPermission(
      user!,
      "org.manageChallenges",
      idea?.ownerChallenge?.organisation,
    );
  const isDeadlineFieldEditable = (fieldId: string) =>
    isFieldEditableUnderDeadline(idea?.submissionDeadline, fieldId, {
      bypass: canBypassDeadlineFieldLock,
    });
  const showAttachments =
    idea?.ownerIdeaTemplate?.enableAttachments &&
    ((canEdit && isEditing) ||
      (idea?.attachments && idea?.attachments?.length > 0));
  const showComments =
    (!isEditing || idea?.commentCount) &&
    !idea?.ownerChallenge?.preventIdeaComments &&
    !hideComments &&
    idea?._id &&
    (!isViewingMergedIdea || (isViewingMergedIdea && !idea?.parent));

  const assessmentFeedback = useMemo(() => {
    const items = ((idea as any)?.assessmentFeedback || []) as Array<any>;
    if (!items.length) return [];

    const rounds = idea?.ownerIdeaTemplate?.assessments?.rounds || [];
    const roundNameById = new Map(rounds.map((r) => [r.id, r.name]));

    const orderedRoundIds = rounds.map((r) => r.id);
    const extraRoundIds = Array.from(
      new Set(items.map((i) => i.assessmentRoundId).filter(Boolean)),
    ).filter((id) => !orderedRoundIds.includes(id));

    const allRoundIds = [...orderedRoundIds, ...extraRoundIds];
    return allRoundIds
      .map((roundId) => {
        const entry = items.find((i) => i.assessmentRoundId === roundId);
        if (!entry) return null;
        return {
          roundId,
          roundName: roundNameById.get(roundId) || "Assessment feedback",
          entry,
        };
      })
      .filter(Boolean) as Array<{
      roundId: string;
      roundName: string;
      entry: any;
    }>;
  }, [idea]);

  const userEstimateImpactSummary = useMemo(() => {
    return idea?.impactSummaries?.userEstimate?.summary?.trim() || "";
  }, [idea?.impactSummaries]);

  const showAutomatedSummary =
    util.hasPermission(user!, "super.viewDashboard") &&
    idea?.ownerChallenge?.aiIdeaSummarisation &&
    (idea?.generalSummary || userEstimateImpactSummary);

  return (
    <>
      {isAuthor && assessmentFeedback.length ? (
        <div>
          {assessmentFeedback.map(({ roundId, roundName, entry }) => (
            <Message key={roundId} style={{ marginBottom: 24 }}>
              <Accordion>
                <AccordionTitle index={0}>
                  {t("common:capitalise", { key: "generic.idea" })} assessment
                  feedback - {roundName}
                </AccordionTitle>

                <AccordionContent index={0}>
                  <Divider fitted style={{ marginBottom: "0.75em" }} />
                  <RichTextViewer value={entry.feedback || ""} />
                </AccordionContent>
              </Accordion>
            </Message>
          ))}
        </div>
      ) : null}
      {idea?.ipStatement?.ipStatementEnabled ? (
        <IPStatementInfo ipStatement={idea?.ipStatement} />
      ) : null}
      {showAutomatedSummary ? (
        <Message
          size="small"
          style={{
            boxShadow:
              "0px 5px 10px 0px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(34,36,38,.22) inset",
          }}
        >
          <MessageHeader
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            Automated {t("generic.idea")} summary
            <Popup
              offset={[13, 2]}
              trigger={<Icon icon={CircleQuestionMarkIcon} />}
            >{`This is an automated summary of this ${t("generic.idea")} based on its own content and the ${t("generic.challenge")} brief. It does not represent an assessment or critique.`}</Popup>
          </MessageHeader>
          <Divider fitted style={{ marginTop: 4, marginBottom: 4 }} />

          {idea?.generalSummary ? (
            <MarkdownViewer value={idea.generalSummary} />
          ) : null}

          {userEstimateImpactSummary ? (
            <>
              <Divider fitted style={{ marginTop: 12, marginBottom: 8 }} />
              <MessageHeader>User {t("generic.impact")} summary</MessageHeader>
              <MarkdownViewer value={userEstimateImpactSummary} />
            </>
          ) : null}
        </Message>
      ) : null}

      {idea?.ownerChallenge?.ideaAnonymity && canEdit ? (
        <Message color="blue">
          <Icon icon={UserIcon} />
          <MessageHeader>You are working anonymously</MessageHeader>
          <MessageContent>
            {`${t("common:capitalise", { key: "generic.ideas" })} in this ${t("generic.challenge")} have their identity hidden to anyone who is not ${t("generic.challengeWithArticle")} administrator. Only you and ${t("generic.challenge")} administrators will be able to see that you created this ${t("generic.idea")}.`}
          </MessageContent>
        </Message>
      ) : null}

      {!idea?.isSubmitted && canEdit && idea?.submissionDeadline ? (
        <Message color="blue">
          <MessageHeader>
            <Icon icon={CalendarIcon} />
            {t("ideas.deadline.banner.header")}
          </MessageHeader>
          <MessageContent>
            <p>
              {t("ideas.deadline.banner.until", {
                deadline: formatDateAt(idea?.submissionDeadline.deadline),
              })}
            </p>
            {idea?.submissionDeadline.note ? (
              <p>
                {t("ideas.deadline.banner.note", {
                  note: idea.submissionDeadline.note,
                })}
              </p>
            ) : null}
            {hasDeadlineFieldRestriction(idea.submissionDeadline) ? (
              <>
                <p>{t("ideas.deadline.banner.editableFields")}</p>
                <List variant="bulleted" compact>
                  {getDeadlineEditableFieldTitles(
                    idea.ownerIdeaTemplate,
                    idea.submissionDeadline.editableFields,
                    t,
                  ).map((title) => (
                    <ListItem key={title}>{title}</ListItem>
                  ))}
                </List>
              </>
            ) : null}
          </MessageContent>
        </Message>
      ) : null}

      {ideaQuery.isLoading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 30 }}>
          <IdeaSection>
            <Card fluid>
              <CardContent>
                <Placeholder>
                  <PlaceholderLine style={{ height: 30 }} />
                  <PlaceholderLine style={{ height: 150 }} />
                </Placeholder>
              </CardContent>
            </Card>
          </IdeaSection>

          {Array(3)
            .fill(0)
            .map((_, index) => (
              <IdeaSection key={index}>
                <Card fluid>
                  <CardContent>
                    <Placeholder>
                      <PlaceholderLine style={{ height: 30 }} />
                    </Placeholder>
                  </CardContent>
                  <CardContent>
                    <Placeholder>
                      <PlaceholderLine style={{ height: 40 }} />
                      <PlaceholderLine style={{ height: 40 }} />
                      <PlaceholderLine style={{ height: 40 }} />
                    </Placeholder>
                  </CardContent>
                </Card>
              </IdeaSection>
            ))}
        </div>
      ) : null}

      {idea ? (
        <IdeaNameField
          isEditingName={
            !!isEditing &&
            isDeadlineFieldEditable(DEADLINE_SPECIAL_FIELD_IDS.name)
          }
          isEditingCover={
            !!isEditing &&
            isDeadlineFieldEditable(DEADLINE_SPECIAL_FIELD_IDS.coverImage)
          }
          ideaId={idea._id}
          ideaName={idea.name}
          ideaCoverImage={util.ideaCoverImage(idea, "2560x400")}
          updateIdea={updateIdea}
          challenge={idea?.ownerChallenge}
        />
      ) : null}
      {idea?.ownerIdeaTemplate?.body?.map(
        (
          section: NonNullable<OpenAPI.Schemas["IdeaTemplate"]["body"]>[number],
          sectionIndex: number,
        ) => {
          if (!util.shouldShowIdeaSection(idea, section)) return null;
          if (
            section.type === "edi" &&
            !util.shouldShowIdeaEdiSection(user!, idea, section)
          ) {
            return null;
          }
          if (section.type === "assessment") {
            if (
              isAssessingSelectedRound &&
              (!assessmentState ||
                !hasCompletedConflictDeclaration ||
                assessmentState?.conflictOfInterest?.hasConflictOfInterest)
            ) {
              return null;
            }
            if (!isAssessingSelectedRound && !urlAssessmentRoundId) {
              return null;
            }

            const sectionAssessmentRoundId =
              section.assessmentRoundId || "legacy";
            if (
              sectionAssessmentRoundId !==
              (selectedAssessmentRoundId || "legacy")
            ) {
              return null;
            }
          }
          if (section.type === "impact") {
            return (
              <>
                <IdeaSection
                  key={section.id}
                  ref={(ref) => setSectionRef?.(ref, "impactStatement")}
                >
                  <Card fluid>
                    <IdeaSectionHeader
                      isAssessor={isAssessingSelectedRound}
                      canViewEdi={false}
                      canEdit={false}
                      isAuthor={false}
                      section={{
                        id: "impactStatement",
                        name: `${section?.name ?? ""}${
                          isEditing &&
                          idea?.ownerChallenge?.impactStatements?.requireOnIdea
                            ? " *"
                            : ""
                        }`,
                        fields: [],
                      }}
                    />
                    <CardContent>
                      <IdeaImpactStatement
                        forStage="userEstimate"
                        editable={
                          !!isEditing &&
                          isDeadlineFieldEditable(
                            DEADLINE_SPECIAL_FIELD_IDS.impactStatement,
                          )
                        }
                      />
                    </CardContent>
                  </Card>
                </IdeaSection>
                <Divider hidden />
              </>
            );
          }
          return (
            <div style={{ marginBottom: 30 }} key={section.id}>
              <IdeaSection
                className={cx(
                  sectionIndex === firstAssessmentSectionIndex &&
                    "assessment-tour-step-5",
                  section.type === "assessment" && "is-assessment",
                )}
                id={section.name}
                ref={(ref) => setSectionRef?.(ref, section.id)}
              >
                <Card fluid>
                  <IdeaSectionHeader
                    section={section}
                    assessment={assessmentState}
                    canViewEdi={canViewEdi}
                    canEdit={canEdit}
                    isAuthor={isAuthor}
                  />
                  <CardContent>
                    {section.type === "businessProfile" ? (
                      <IdeaBusinessProfile
                        isEditing={
                          !!isEditing &&
                          isDeadlineFieldEditable(
                            DEADLINE_SPECIAL_FIELD_IDS.businessProfile,
                          )
                        }
                        updateIdea={updateIdea}
                        setFieldRef={setFieldRef}
                      />
                    ) : (
                      <Custom
                        section={section}
                        isEditing={isEditing}
                        updateIdea={updateIdea}
                        externalChanges={externalChanges}
                        emptyRequiredField={props.emptyRequiredField}
                        setFieldRef={setFieldRef}
                      />
                    )}
                  </CardContent>
                </Card>
              </IdeaSection>
            </div>
          );
        },
      )}

      {isAssessingSelectedRound ? (
        <div style={{ marginBottom: 30 }}>
          {assessmentState?.isSubmitted ? (
            <Label color="green">Assessment submitted</Label>
          ) : (
            <TooltipButton
              style={{ marginLeft: 3 }}
              onClick={submitAssessment}
              disabled={!assessmentIsSubmissable}
              tooltip={
                assessmentIsSubmissable
                  ? undefined
                  : "To submit an assessment you either have to complete all sections of the assessment or declare a conflict of interest"
              }
              loading={isSavingAssessment}
              color="primary"
            >
              {assessmentState?.conflictOfInterest?.hasConflictOfInterest ===
              true
                ? "Submit assessment with conflict of interest"
                : "Submit assessment"}
            </TooltipButton>
          )}
        </div>
      ) : null}

      {!theme.sizes.isComputer ? (
        <SideNavContainer style={{ marginBottom: 20 }}>
          <H3>Completion</H3>
          {idea?.ownerIdeaTemplate?.body?.map(
            (
              section: NonNullable<
                OpenAPI.Schemas["IdeaTemplate"]["body"]
              >[number],
              idx: number,
            ) => {
              const sectionName = section.name ?? "";
              if (!util.shouldShowIdeaSection(idea, section)) {
                return null;
              }
              if (section.type === "assessment" && !assessmentState) {
                return null;
              }
              const submissibility = getSectionSubmissibility(idea, section);
              const color = getSubmissibilityColor(submissibility);

              return (
                <SideNavLink
                  key={`${sectionName}-${idx}`}
                  content={
                    sectionName.charAt(0).toUpperCase() + sectionName.slice(1)
                  }
                  iconRight={
                    submissibility && color ? (
                      <CircularProgress
                        size={16}
                        color={color}
                        progress={getSubmissibilityProgress(submissibility)}
                        strokeWidth={1.5}
                        icon={<Icon icon={CheckIcon} color="green" />}
                        iconVisible={isSubmissibilitySectionComplete(
                          submissibility,
                        )}
                      />
                    ) : undefined
                  }
                  onClick={() =>
                    scrollToSection?.(getSubmissibilitySectionKey(section))
                  }
                />
              );
            },
          )}
        </SideNavContainer>
      ) : null}

      {idea?.parent || isAssessingSelectedRound || idea?.children ? null : (
        <SubmitIdeaButton
          updateIdea={updateIdea}
          onSubmit={onSubmit ?? (() => {})}
          isSaving={isSaving}
          unsaved={unsaved}
        />
      )}

      {showAttachments ? (
        <div>
          <div
            ref={(ref) => setSectionRef?.(ref, "attachments")}
            id="attachments"
          >
            <Divider section hidden />
            <IdeaAttachments isEditing={isEditing} updateIdea={updateIdea} />
          </div>
        </div>
      ) : null}
      {showComments ? (
        <div>
          <div
            ref={(ref) => setSectionRef?.(ref, "comments")}
            id="idea-comments"
          >
            <Divider hidden />
            <H4>{t("generic.comments")}</H4>
            <IdeaComments forId={idea?._id} />
          </div>
        </div>
      ) : null}
    </>
  );
};

export default BodyContainer;
